/**
 * Conversion context.
 *
 * The source keeps per-conversion state in module-level mutable globals
 * (: `documentMetadata`, `globalFootnotesHtml`). That makes the
 * engine non-reentrant — two conversions in flight corrupt each other, and test order
 * becomes significant.
 *
 * The port threads this object through the pipeline instead. This is an explicit,
 * allowed deviation under CLAUDE.md § Code standards: it is not observable in the
 * emitted HTML or CSS, because the source is only ever called serially from a single
 * React effect.
 */
import type { DocumentMetadata, DocumentSettings, MacroDefinition } from './types'

export interface ConversionContext {
  /** From \title, \author, \date — consumed by \maketitle. Source: `documentMetadata`. */
  metadata: DocumentMetadata

  /** Built by the early footnote pass, consumed by \afficherEndNotes. Source: `globalFootnotesHtml`. */
  endnotesHtml: string

  /** Running counter for footnote/endnote marks. */
  noteCounter: number

  /** Macros harvested from the preamble by the macro-extraction stage. */
  macros: MacroDefinition[]

  /** Settings harvested from the preamble; drives both HTML and CSS generation. */
  settings: DocumentSettings
}

/** The font stack used when the preamble names no recognised font package. */
export const DEFAULT_FONT_SETTINGS = {
  mainFont: "'Merriweather', Georgia, serif",
  mainFontImport: 'Merriweather:ital,wght@0,300;0,400;0,700;1,400',
} as const

export function createContext(settings?: Partial<DocumentSettings>): ConversionContext {
  return {
    metadata: { title: null, author: null, date: null },
    endnotesHtml: '',
    noteCounter: 0,
    macros: [],
    settings: {
      fonts: { ...DEFAULT_FONT_SETTINGS },
      ...settings,
    },
  }
}
